import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { 
  LayoutDashboard, UtensilsCrossed, Menu as MenuIcon, 
  ReceiptText, FileBarChart, Settings as SettingsIcon, LogOut, Search, Printer, ArrowLeft, X
} from "lucide-react";
import { ChevronRight, ChevronLeft } from "lucide-react";
import "./Billing.css";
import logo from "../assets/lg.png";

export default function Billing() {
  const navigate = useNavigate();
  const { tableId } = useParams(); 
  
  const [searchQuery, setSearchQuery] = useState("");
  const [gst, setGst] = useState(false);
  const [billItems, setBillItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All Categories");
  const [showReceipt, setShowReceipt] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Added for mobile menu
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // 1. Fetch Menu from Database
  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/menu.php`);
        setMenuItems(res.data);
      } catch (err) {
        console.error("Error loading menu from DB");
      }
    };
    fetchMenu();

    if (tableId) {
      const allOrders = JSON.parse(localStorage.getItem("activeTableOrders") || "{}");
      if (allOrders[tableId]) {
        setBillItems(allOrders[tableId]);
      }
    }
  }, [tableId]);

  const subtotal = billItems.reduce((acc, i) => acc + (i.price * i.qty), 0);
  const gstAmount = subtotal * 0.05;
  const grandTotal = gst ? subtotal + gstAmount : subtotal;

  // 2. Settle Bill (Saves to MySQL 'sales' table)
  const handleSettleBill = async () => {
    let nextNumber = 1;
    try {
      const res = await axios.get(`${API_BASE_URL}/reports.php`);
      nextNumber = res.data.length + 1;
    } catch (e) { console.error(e); }

    const paddedNumber = nextNumber.toString().padStart(4, '0');
    const generatedId = tableId && tableId !== "Walk-in" 
      ? `T${tableId}-${paddedNumber}` 
      : `S-WALK-${paddedNumber}`;

    const billData = {
      id: generatedId,
      tableId: tableId || "Walk-in",
      itemsCount: billItems.reduce((acc, item) => acc + item.qty, 0),
      items: JSON.stringify(billItems), 
      subtotal: subtotal,
      gstAmount: gst ? gstAmount : 0,
      finalAmount: Math.round(grandTotal)
    };

    try {
      const res = await axios.post(`${API_BASE_URL}/billing.php`, billData);
      if (res.data.status === "success") {
        if (tableId) {
          const allOrders = JSON.parse(localStorage.getItem("activeTableOrders") || "{}");
          delete allOrders[tableId];
          localStorage.setItem("activeTableOrders", JSON.stringify(allOrders));
        }
        alert(`Bill ${generatedId} Settled and Saved to Database!`);
        window.print();
        navigate("/reports");
      }
    } catch (err) {
      alert("Error saving bill to database.");
    }
  };

  const handleSaveOrder = () => {
    if (!tableId) {
      alert("Please select a table from the Tables tab first.");
      return;
    }
    const allOrders = JSON.parse(localStorage.getItem("activeTableOrders") || "{}");
    allOrders[tableId] = billItems;
    localStorage.setItem("activeTableOrders", JSON.stringify(allOrders));
    alert(`Changes saved for Table ${tableId}`);
    navigate("/tables");
  };

  const updateQty = (item, delta) => {
    const existing = billItems.find(i => i.id === item.id);
    if (existing) {
      const newQty = existing.qty + delta;
      if (newQty <= 0) {
        setBillItems(billItems.filter(i => i.id !== item.id));
      } else {
        setBillItems(billItems.map(i => i.id === item.id ? { ...i, qty: newQty } : i));
      }
    } else if (delta > 0) {
      setBillItems([...billItems, { ...item, qty: 1 }]);
    }
  };

  if (showReceipt) {
    return (
      <div className="dash-container">
        <main className="main-content" style={{ background: '#f9fafb', display: 'flex', alignItems: 'center' }}>
          <div className="receipt-paper">
            <h2 className="receipt-hotel-name">Hotel Tuljai</h2>
            <p className="receipt-subtitle">Billing Receipt</p>
            
            <div className="receipt-meta">
              <div>
                <span style={{ color: '#a1a1aa', display: 'block', fontSize: '12px' }}>Order Type:</span>
                <strong>{tableId ? `Table ${tableId}` : "Walk-in"}</strong>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ color: '#a1a1aa', display: 'block', fontSize: '12px' }}>Date:</span>
                <strong>{new Date().toLocaleDateString()}</strong>
              </div>
            </div>

            <table className="receipt-table">
              <thead>
                <tr>
                  <th className="col-name">Item</th>
                  <th className="col-qty">Qty</th>
                  <th className="col-price">Price</th>
                  <th className="col-total">Total</th>
                </tr>
              </thead>
              <tbody>
                {billItems.map((item) => (
                  <tr key={item.id}>
                    <td className="col-name">{item.name}</td>
                    <td className="col-qty">{item.qty}</td>
                    <td className="col-price">₹{item.price}</td>
                    <td className="col-total">₹{item.price * item.qty}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="receipt-divider"></div>

            <div className="receipt-total-row">
              <span>Subtotal</span>
              <span>₹{subtotal.toFixed(2)}</span>
            </div>
            
            {gst && (
              <div className="receipt-total-row">
                <span>GST (5%)</span>
                <span>₹{gstAmount.toFixed(2)}</span>
              </div>
            )}

            <div className="receipt-grand-total">
              <span>Grand Total</span>
              <span style={{ color: '#845738' }}>₹{grandTotal.toFixed(2)}</span>
            </div>

            <p className="thanks-msg">Thank you for dining with us!</p>

            <div className="receipt-actions">
              <button className="print-btn" onClick={handleSettleBill}>
                <Printer size={20} /> Print & Settle
              </button>
              <button className="back-btn" onClick={() => setShowReceipt(false)}>
                <ArrowLeft size={20} /> Back
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="dash-container">
     <aside className={`sidebar ${isSidebarOpen ? 'active' : ''} ${isSidebarCollapsed ? 'collapsed' : ''}`}>

  {/* COLLAPSE BUTTON */}
  <button 
    className="sidebar-toggle-btn d-none d-md-flex"
    onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
  >
    {isSidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
  </button>

  <div className="sidebar-top">

    {/* ✅ LOGO */}
    <div className="brand">
      <img src={logo} alt="Logo" className="sidebar-logo" />

      {/* MOBILE CLOSE */}
      <X 
        className="d-md-none ms-auto cursor-pointer" 
        onClick={() => setIsSidebarOpen(false)} 
      />
    </div>

    {/* NAVIGATION */}
    <nav className="nav-menu">
      <div className="nav-item" onClick={() => navigate("/dashboard")}>
        <LayoutDashboard size={20} /> {!isSidebarCollapsed && "Dashboard"}
      </div>

      <div className="nav-item" onClick={() => navigate("/tables")}>
        <UtensilsCrossed size={20} /> {!isSidebarCollapsed && "Tables"}
      </div>

      <div className="nav-item" onClick={() => navigate("/menu")}>
        <MenuIcon size={20} /> {!isSidebarCollapsed && "Menu"}
      </div>

      <div className="nav-item active" onClick={() => navigate("/billing")}>
        <ReceiptText size={20} /> {!isSidebarCollapsed && "Billing"}
      </div>

      <div className="nav-item" onClick={() => navigate("/reports")}>
        <FileBarChart size={20} /> {!isSidebarCollapsed && "Reports"}
      </div>
    </nav>
  </div>

  {/* BOTTOM */}
  <div className="sidebar-bottom">
    <div className="nav-item" onClick={() => navigate("/settings")}>
      <SettingsIcon size={20} /> {!isSidebarCollapsed && "Settings"}
    </div>

    <div className="nav-item logout-red" onClick={() => navigate("/")}>
      <LogOut size={20} /> {!isSidebarCollapsed && "Logout"}
    </div>
  </div>

</aside>

      <main className="main-content">
        {/* Added mobile-top-bar */}
        <div className="mobile-top-bar d-md-none d-flex justify-content-between align-items-center mb-3 bg-white p-3 rounded shadow-sm">
          <div className="brand-text">Dine Spark</div>
          <MenuIcon className="cursor-pointer" onClick={() => setIsSidebarOpen(true)} />
        </div>

        <header className="page-header">
          <div className="header-titles">
            <h1 className="serif-title">Billing {tableId ? `- Table ${tableId}` : ""}</h1>
            <p className="subtitle">Managing items for {tableId ? `Table ${tableId}` : "New Order"}</p>
          </div>
        </header>

        <div className="billing-grid">
          <div className="left-panel">
            <div className="top-box">
              <span>Service Type: </span>
              <strong>{tableId ? `Table ${tableId}` : "General Service"}</strong>
            </div>

            <div className="search-row">
              <div className="search-wrapper">
                <Search size={18} className="search-icon" />
                <input 
                  type="text" 
                  placeholder="Search menu items..." 
                  value={searchQuery} 
                  onChange={(e) => setSearchQuery(e.target.value)} 
                />
              </div>
              <select className="all-categories-pill" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
                <option>All Categories</option>
                <option>Starter</option>
                <option>Main Course</option>
                <option>Dessert</option>
                <option>Beverage</option>
              </select>
            </div>

            <div className="menu-grid">
              {menuItems
                .filter(i => i.name.toLowerCase().includes(searchQuery.toLowerCase()) && (selectedCategory === "All Categories" || i.category === selectedCategory))
                .map(item => {
                  const cartItem = billItems.find(b => b.id === item.id);
                  return (
                    <div key={item.id} className="menu-card">
                      <div><div className="menu-name">{item.name}</div><div className="menu-price">₹{item.price}</div></div>
                      {!cartItem ? (
                        <button className="add-btn" onClick={() => updateQty(item, 1)}>+ Add</button>
                      ) : (
                        <div className="qty-controls">
                          <button onClick={() => updateQty(item, -1)}>−</button>
                          <span>{cartItem.qty}</span>
                          <button onClick={() => updateQty(item, 1)}>+</button>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>

          <div className="bill-card">
            <h3>Order Summary</h3>
            <div className="bill-items-list">
              {billItems.map(item => (
                <div key={item.id} className="bill-row">
                  <span>{item.name} × {item.qty}</span>
                  <span>₹{item.price * item.qty}</span>
                </div>
              ))}
            </div>
            
            <div className="bill-row subtotal-row" style={{ marginTop: '15px', fontWeight: 'bold' }}>
              <span>Subtotal</span>
              <span>₹{subtotal}</span>
            </div>

            <div className="gst-box" onClick={() => setGst(!gst)}>
              <div className={`gst-circle ${gst ? "active" : ""}`} />
              Apply GST (5%)
            </div>

            {gst && (
              <div className="bill-row gst-display" style={{ color: "#666", fontSize: "0.9rem", marginBottom: '10px' }}>
                <span>GST (5%)</span>
                <span>+ ₹{gstAmount.toFixed(2)}</span>
              </div>
            )}

            <div className="grand-total-row">
              <span>Grand Total</span>
              <span>₹{grandTotal.toFixed(2)}</span>
            </div>

            <button className="bill-action-btn primary" onClick={() => billItems.length > 0 && setShowReceipt(true)}>Generate Bill</button>
            <button className="bill-action-btn secondary" onClick={handleSaveOrder}>Save Changes</button>
          </div>
        </div>
      </main>
    </div>
  );
}